<template>
  <Card v-if="loading" :loadingState="true"></Card>
  <div v-else-if="showTeaser && jsonTeaser">
    <div
      :class="[
        { 'is-styled': !disableStyles },
        { 'promotion-area--hover': hasHover },
        { 'promotion-area--centered': jsonTeaser.settings.teaserCentered },
        { 'promotion-area--stage-teaser': stageTeaser }
      ]"
      class="promotion-area"
    >
      <div class="promotion-area__content">
        <div
          :style="{
            backgroundColor:
              jsonTeaser.image.backgroundColor === ''
                ? 'white'
                : jsonTeaser.image.backgroundColor
          }"
          class="promotion-area__contentTop"
        >
          <img
            loading="lazy"
            :src="`${jsonTeaser.image.url}`"
            :alt="`${jsonTeaser.image.alt}`"
          />
          <span v-if="jsonTeaser.image.tag" class="promotion-area__banner">{{
            jsonTeaser.image.tag
          }}</span>
        </div>
        <div
          :style="{
            backgroundColor:
              jsonTeaser.content.backgroundColor === ''
                ? 'white'
                : jsonTeaser.content.backgroundColor
          }"
          class="promotion-area__contentBottom"
        >
          <p v-if="jsonTeaser.content.headline != ''" class="promotion-area__contentText">
            {{ jsonTeaser.content.headline }}
          </p>
          <p
            v-if="jsonTeaser.content.text != ''"
            class="promotion-area__contentTextSmall"
            v-dompurify-html="jsonTeaser.content.text"
          ></p>
          <div
            v-for="button in jsonTeaser.buttons"
            class="button-spacing"
            :key="button.label"
          >
            <CustomButton
              @click="() => (jsonTeaser ? redirect(button) : null)"
              :buttonColor="
                button.color.toLowerCase() === 'primary'
                  ? ButtonColor.PRIMARY
                  : button.color.toLowerCase() === 'secondary'
                    ? ButtonColor.SECONDARY
                    : button.color.toLowerCase() === 'tertiary'
                      ? ButtonColor.TERTIARY
                      : button.color.toLowerCase() === 'link'
                        ? ButtonColor.LINK
                        : ButtonColor.LINK_BIG
              "
              >{{ button.label }}</CustomButton
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { isRedCooperationPartner } from '@/utilsTenant/RedCooperationUtils'
import { ButtonColor, Card, CustomButton } from 'de-zurich-ui-library'
import type { Teaser, TeaserButton } from '@/types/rahmen/sitecore-teaser'
import { isDark, isSitecoreTeaser } from '@/utils/utils'
import { handleTeaserButtonNavigation } from '@/utils/NavigationUtils'

// Define the prop and emits
const props = defineProps({
  jsonFileName: {
    type: String,
    required: true
  },
  stageTeaser: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (event: 'click'): void
  (event: 'click2'): void
}>()

const hasHover = ref(false)
const disableStyles = ref(false)
const loading = ref(true)
const loaded = ref(false)
const jsonTeaser = ref<Teaser | undefined>()
const showJsonTeaser = ref(false)
const isRedCooperationPartnerVal = isRedCooperationPartner()

const redirect = (button: TeaserButton) => {
  if (button.url == 'returnClick') {
    emit('click')
    return
  }
  if (button.url == 'returnClick2') {
    emit('click2')
    return
  }
  handleTeaserButtonNavigation(button.url, button.target)
}

const isDarkBackground = computed(() => {
  return showJsonTeaser.value && isDark(jsonTeaser.value?.content?.backgroundColor || '')
})

const textColor = computed(() => {
  return showJsonTeaser.value && isDarkBackground.value
    ? 'white'
    : 'var(--primary-color-0)'
})

const tagColor = computed(() => {
  if (!showJsonTeaser.value) return '#1498eb'
  return jsonTeaser.value?.image.tagColor === ''
    ? '#1498eb'
    : (jsonTeaser.value?.image.tagColor ?? '#1498eb')
})

const showTeaser = computed(() => {
  if (!showJsonTeaser.value || !jsonTeaser.value || loading.value) return false
  if (jsonTeaser.value.settings.hide) return false
  return !(isRedCooperationPartnerVal && jsonTeaser.value.settings.isAdvertisement)
})

onMounted(async () => {
  try {
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SITECORE.BASE_URL_JSS}${globalThis.ENV_CONFIG?.SITECORE.JSON_URL_JSS}?name=${props.jsonFileName}`
    )

    if (!response.ok) {
      // Stop loading state even if failed; silently do not show teaser
      loading.value = false
      return
    }

    jsonTeaser.value = await response.json()
    if (isSitecoreTeaser(jsonTeaser.value)) {
      showJsonTeaser.value = true
    }
    loaded.value = true
  } catch (error) {
    console.log('Error fetching teaser', error)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" scoped>
.is-styled {
  .promotion-area {
    width: 100%;
    margin-bottom: 16px;
    display: flex;

    .button-spacing:not(:last-child) {
      margin-bottom: 16px;
    }

    p {
      color: v-bind(textColor);
      word-break: break-word;
    }

    .button {
      width: var(--sitecore-teaser-button-width);
    }

    &--centered {
      align-items: center;
      text-align: center;

      &__content {
        text-align: center;
      }
    }

    &--hover {
      transition: background-color 0.25s ease-in;

      &:hover,
      &:focus {
        cursor: pointer;
      }
    }

    &__content {
      min-height: 100px;
      width: 100%;
      border-radius: 20px;
      position: relative;

      img {
        max-width: 100%;
        border-radius: 20px;
        padding: var(--sitecore-teaser-image-padding);
      }
    }

    &__contentTop {
      border-radius: 20px 20px 0 0;
      display: flex;
      justify-content: center;
    }

    &__contentBottom {
      background-color: white;
      padding: var(--sitecore-teaser-contentBottom-padding);
      border-radius: 0 0 20px 20px;

      p:last-child {
        margin-bottom: 0;
      }
    }

    &__contentText {
      font-size: var(--sitecore-teaser-contentText-font-size);
      line-height: var(--sitecore-teaser-contentText-line-height);
      font-family: var(--sitecore-teaser-contentText-font-familiy);
      color: var(--sitecore-teaser-contentText-color);
    }

    &__contentTextSmall {
      margin-bottom: 24px;
    }

    &__banner {
      position: absolute;
      left: 16px;
      top: 16px;
      background-color: v-bind(tagColor);
      color: white !important;
      border-radius: 4px;
      padding: 4px 8px;
    }
  }

  .promotion-area--stage-teaser {
    .promotion-area__content {
      img {
        padding: 40px 40px 0 40px !important;
      }
    }

    .button {
      width: 100%;
    }
  }
}
</style>
